import React, { useState } from 'react';
import { ContentLayout, MainLayout } from 'components/Layout';
import { useAuth } from 'lib/auth';
import { Button } from 'components/Elements';
import { TextAreaField } from 'components/Form';
import { CheckCircle2, SendHorizonal } from 'lucide-react';

const SuggestionList = ({ suggestions }) => {
  if (!suggestions.length) {
    return (
      <p className="text-white/50 text-sm">No suggestions submitted yet.</p>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {suggestions.map(({ user, message, submittedAt }, idx) => (
        <div
          key={idx}
          className="bg-white/5 border border-white/10 backdrop-blur-md p-4 rounded-xl shadow transition-all"
        >
          <p className="text-sm text-white/80 mb-1">{message}</p>
          <div className="flex justify-between text-xs text-white/40">
            <span>{user?.name || 'Anonymous'}</span>
            <span>{submittedAt}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SuggestionBox = () => {
  const { user } = useAuth();
  const [suggestion, setSuggestion] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const handleSubmit = () => {
    if (!suggestion.trim()) return;

    const newEntry = {
      user: {
        name: user?.name || 'Anonymous',
        email: user?.email || '',
      },
      message: suggestion.trim(),
      submittedAt: new Date().toLocaleString(),
    };

    setSuggestions([newEntry, ...suggestions]);
    setSuggestion('');
    setSubmitted(true);

    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <MainLayout>
      <ContentLayout
        nobg={true}
        title="Suggestion Box"
        subTitle="We value your input"
      >
        <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-6 rounded-2xl max-w-2xl w-full mx-auto shadow-lg">
          <h2 className="text-white/90 text-lg font-semibold mb-2">
            Submit a Suggestion
          </h2>
          <p className="text-white/50 text-sm mb-4">
            Got feedback or an idea? We'd love to hear it. Be specific and kind.
          </p>

          <TextAreaField
            placeholder="Type your suggestion here..."
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            rows={4}
            className="bg-transparent border-white/20 text-white placeholder-white/30"
          />

          <div className="flex justify-end mt-4">
            <Button
              onClick={handleSubmit}
              disabled={!suggestion.trim()}
              variant="darkGlass"
              className="flex items-center gap-2"
            >
              <SendHorizonal className="w-4 h-4" />
              Submit
            </Button>
          </div>

          {submitted && (
            <div className="mt-4 flex items-center text-green-400 text-sm gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Suggestion submitted!
            </div>
          )}
        </div>

        <SuggestionList suggestions={suggestions} />
      </ContentLayout>
    </MainLayout>
  );
};
